package com.siriusxm.unifiedcontent.console.player

import com.siriusxm.pia.TokenAccess
import com.siriusxm.pia.player.PlaybackClock
import com.siriusxm.pia.player.PlaybackDetailsUpdate
import com.siriusxm.pia.player.PlayingDetails
import com.siriusxm.unifiedcontent.epg.EPGService
import contentingestion.unifiedmodel.EntityType
import contentingestion.unifiedmodel.LinearCut
import contentingestion.unifiedmodel.ReferenceIdentifiable
import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.serialization.kotlinx.json.json
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import unifiedcontent.aggregator.Aggregator
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Instant

private val cutJson = Json { ignoreUnknownKeys = true }

/**
 * Builds the `channelCuts` lookup for [SdkCMCPlayer]: resolves a catalog source channel id to its
 * EPG channel reference through Aggregator `getMapping`, then reads the three most recent cuts from
 * the EPG service. The caller's own token is attached by [auth], so the browser needs no server hop.
 *
 * Everything is resolved lazily on first use ([aggregator] and [epgApiUrl] are read at lookup time)
 * because the host constructs its players before the application context is initialized. The
 * authenticated Ktor client is created once and reused for all lookups.
 */
public fun channelCutsLookup(
    aggregator: () -> Aggregator,
    epgApiUrl: () -> String,
    auth: () -> TokenAccess,
): suspend (String) -> List<LinearCut> {
    val client by lazy {
        HttpClient {
            expectSuccess = true
            install(ContentNegotiation) { json(Json { ignoreUnknownKeys = true }) }
            install(HttpTimeout) { requestTimeoutMillis = 5000 }
            auth().installAuth(this)
        }
    }
    return { sourceChannelId ->
        // Catalog entities use source IDs, while EPG indexes resolved channel references.
        val mapping = aggregator().getMapping(sourceChannelId)
        EPGService(epgApiUrl(), client).getCutsByChannel(mapping.atlasId, max = 3, order = "desc").entities
    }
}

/** A cut and its effective broadcast interval, independent of API/transport concerns. */
internal data class PlayingCut(val cut: LinearCut, val start: Instant, val end: Instant?)

/**
 * Build broadcast intervals from a known duration or the next broadcast boundary,
 * whichever is earlier, so a duration gap cannot leave an old song visible. An absent duration is
 * valid in the EPG model; without a following cut the end remains unknown. Malformed source values
 * fail the lookup rather than being skipped or replaced with an invented interval.
 */
internal fun playbackCutTimeline(cuts: List<LinearCut>): List<PlayingCut> {
    val ordered = cuts.map { cut ->
        cut to Instant.parse(cut.timestamp)
    }.sortedBy { it.second }
    return ordered.mapIndexed { index, (cut, start) ->
        val nextStart = ordered.getOrNull(index + 1)?.second
        val durationEnd = cut.duration?.let { duration ->
            require(duration > 0) { "Cut ${cut.id} has a non-positive duration" }
            start + duration.milliseconds
        }
        val end = listOfNotNull(nextStart, durationEnd).minOrNull()
        PlayingCut(cut, start, end)
    }
}

/** Select by the audio timestamp, never by which cut arrived most recently. */
internal fun selectPlayingCut(cuts: List<PlayingCut>, timestamp: Instant): PlayingCut? =
    cuts.lastOrNull { it.start <= timestamp && (it.end == null || timestamp < it.end) }

/**
 * Turn a channel's recent [cuts] into a [PlaybackDetailsUpdate]. The current item is chosen by the
 * audio's [PlaybackClock.broadcastTime] (null until the stream supplies one); the timeline carries
 * every interval so the controller can keep selecting locally as audio advances. [channelImageUrl] is
 * the channel artwork used when a cut has none, and [artworkUrl] converts storage keys to loadable
 * URLs. Malformed cut data throws [IllegalArgumentException] so the controller retries.
 */
internal fun channelPlaybackDetails(
    cuts: List<LinearCut>,
    channelImageUrl: String?,
    clock: PlaybackClock,
    artworkUrl: (String?) -> String?,
): PlaybackDetailsUpdate {
    val timeline = playbackCutTimeline(cuts)
    fun PlayingCut.toDetails() = PlayingDetails(
        entityId = cut.id,
        title = cut.name,
        subtitle = cut.artist,
        description = cut.album,
        imageUrl = artworkUrl(cutJson.encodeToJsonElement(cut).jsonObject.tileUrl() ?: channelImageUrl),
        startsAt = start,
        endsAt = end,
        approximate = false,
        relatedEntities = listOfNotNull(
            cut.show?.let { ReferenceIdentifiable(EntityType.SHOW, it) },
            cut.episode?.let { ReferenceIdentifiable(EntityType.EPISODE_LINEAR, it) },
        ),
    )
    return PlaybackDetailsUpdate(
        current = clock.broadcastTime?.let { selectPlayingCut(timeline, it)?.toDetails() },
        pollAfterMilliseconds = 10_000,
        timeline = timeline.map { it.toDetails() },
    )
}

/** The aspect-1x1 tile artwork URL of an entity JSON object, or null. */
private fun JsonObject.tileUrl(): String? =
    (get("images") as? JsonObject)?.get("tile")?.let { it as? JsonObject }
        ?.get("aspect_1x1")?.let { it as? JsonObject }
        ?.get("default")?.let { it as? JsonObject }
        ?.get("url")?.jsonPrimitive?.contentOrNull
